/**
 * Legacy role registry. These are roles the backend seeds as system roles
 * but whose effect is hard-coded in the frontend (feature gates, not RBAC
 * permissions). They render in the role list with a "Legacy" pill and in
 * the editor with a one-paragraph note instead of the matrix grid — they
 * carry no editable grants by design.
 *
 * The slug strings here are the contract the FE checks against. The source
 * of truth for what each legacy role *does* is the FE file that references
 * its slug (e.g. lockedFeatures.js for `distributor`). This registry just
 * tells the role-admin UI to render them differently from regular roles.
 *
 * If you add a new legacy role here, also add it to the corresponding FE
 * feature gate that interprets the role. (Role creation is not self-serve —
 * slug collisions are the backend's concern when provisioning roles.)
 */

import i18n from 'i18n';

// Localized via getters so the export shape (and the `distributor` slug key
// that LEGACY_ROLE_SLUGS / isLegacyRole read) stays byte-for-byte identical
// for every consumer, while the display strings resolve in the active locale.
export const LEGACY_ROLE_NOTES = Object.freeze({
  distributor: {
    get title() {
      return i18n.t('admin:legacyRoles.distributor.title', 'Distributor — legacy tier');
    },
    get description() {
      return i18n.t(
        'admin:legacyRoles.distributor.description',
        'When assigned to a member, the frontend locks the features below for that user. The effect is hard-coded in feature gates; this role intentionally carries no permissions of its own, so the permission matrix is hidden.',
      );
    },
    get lockedFeatures() {
      return [
        i18n.t('admin:legacyRoles.distributor.features.premiums', 'Premiums'),
        i18n.t('admin:legacyRoles.distributor.features.exports', 'Exports'),
        i18n.t('admin:legacyRoles.distributor.features.diagrams', 'Diagrams'),
        i18n.t('admin:legacyRoles.distributor.features.ownedInstallations', 'Owned Installations'),
        i18n.t('admin:legacyRoles.distributor.features.teamManagement', 'Team Management'),
      ];
    },
  },
});

export const LEGACY_ROLE_SLUGS = Object.freeze(new Set(Object.keys(LEGACY_ROLE_NOTES)));

export const isLegacyRole = (slug) => LEGACY_ROLE_SLUGS.has(slug);

export const getLegacyRoleNote = (slug) => LEGACY_ROLE_NOTES[slug] || null;
